import { useAppSelector } from "../../../../../redux/store";
import { DateObject } from "react-multi-date-picker";
import { PaymentStatusModel } from "../../../../shared/models/order/payment-status.model";
import {
  BarekamIcon,
  IconVariants,
} from "../../../../components/icon/barekam-icon";
import BarekamButton, { SimpleButtons } from "../../../../components/button";
import { FilterOpened, FilterOpenedMobile } from "../styled";
import { PaymentTypeModel } from "../../../../shared/models/order/payment-type.model";
import { Recipient } from "../../../../shared/models/user/recipient/recipient";
import { OrderFilterModel } from "../../../../shared/models/order/order-filter.model";
import BarekamText from "../../../../components/text";
import { ProductCategoryModel } from "../../../../../redux/reducers/static-data.reducers";
import {
  Message,
  MessageSections,
} from "../../../../components/message/message";
import RecipientFilter from "./blocks/recipient-filter/recipient-filter";
import TaxesFilter from "./blocks/taxes-filter/taxes-filter";
import PaymentTypeFilter from "./blocks/payment-type-filter/payment-type-fliter";
import PaymentStatusFilter from "./blocks/payment-status-filter/payment-status-filter";
import DateFilter from "./blocks/date-filter/date-filter";
import CategoryFilter from "./blocks/category-filter/category-filter";
import { useState } from "react";
import { FilterDialog } from "../../../../components/dialogs/filter-dialog/filter-dialog";

export const OrderFilter = ({
  preLoadedFilter,
  onFilterChange,
  mobileFilterOpened,
  setMobileFilterOpened,
}: OrderFilterProps) => {
  const [filter, setFilter] = useState<OrderFilterModel>(preLoadedFilter || {});

  const { paymentStatuses, paymentTypes, productCategories } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const { myRecipientsData } = useAppSelector(
    (state) => state.recipientDataReducer,
  );

  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  function onDateFilterChange(dates: DateObject[]) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (dates.length === 1) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      modifiedFilter.minEnteredDate = new Date(dates[0]).getTime();
      modifiedFilter.maxEnteredDate = new Date(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dates[0].add(1, "day"),
      ).getTime();
    } else if (dates.length === 2) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      modifiedFilter.minEnteredDate = new Date(dates[0]).getTime();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      modifiedFilter.maxEnteredDate = new Date(dates[1]).getTime();
    } else {
      delete modifiedFilter.minEnteredDate;
      delete modifiedFilter.maxEnteredDate;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function onPaymentStatusFilterChange(
    paymentFilter: PaymentStatusModel | undefined,
  ) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (paymentFilter) {
      modifiedFilter.paymentStatus = paymentFilter.paymentStatusId;
    } else {
      delete modifiedFilter.paymentStatus;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function onRecipientFilterChange(recipient: Recipient | undefined) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (recipient) {
      modifiedFilter.recipient = recipient.recipientId;
    } else {
      delete modifiedFilter.recipient;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function onCategoryFilterChange(
    productCategory: ProductCategoryModel | undefined,
  ) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (productCategory) {
      modifiedFilter.productCategory = productCategory.productCategoryId;
    } else {
      delete modifiedFilter.productCategory;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function onPaymentTypeFilterChange(typeFilter: PaymentTypeModel | undefined) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (typeFilter) {
      modifiedFilter.paymentType = typeFilter.paymentTypeId;
    } else {
      delete modifiedFilter.paymentType;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function onTaxesFilterChange(withTaxes: boolean | undefined) {
    const modifiedFilter: OrderFilterModel = { ...filter };

    if (withTaxes !== undefined) {
      modifiedFilter.withTaxes = withTaxes;
    } else {
      delete modifiedFilter.withTaxes;
    }

    setFilter(modifiedFilter);
    onFilterChange(modifiedFilter);
  }

  function resetFilter() {
    setFilter({});
    onFilterChange({});

    setMobileFilterOpened(false);
  }

  const FilterResetButton = () => {
    return (
      <BarekamButton type={SimpleButtons.LINK} onClick={() => resetFilter()}>
        <BarekamIcon variant={IconVariants.reset} />

        <BarekamText variant={"m"} bold color={"#A9A9C2"}>
          <Message messageKey={"reset"} sectionId={MessageSections.ORDERS} />
        </BarekamText>
      </BarekamButton>
    );
  };

  const FilterContent = () => {
    return (
      <>
        <DateFilter
          minEnteredDate={filter.minEnteredDate}
          maxEnteredDate={filter.maxEnteredDate}
          onFilterChange={onDateFilterChange}
        />
        <PaymentStatusFilter
          selectedFilterId={filter.paymentStatus}
          paymentStatuses={paymentStatuses}
          onFilterChange={onPaymentStatusFilterChange}
        />
        <RecipientFilter
          selectedFilterId={filter.recipient}
          myRecipients={myRecipientsData.recipients}
          onFilterChange={onRecipientFilterChange}
        />
        <CategoryFilter
          selectedFilterId={filter.productCategory}
          productCategories={productCategories}
          onFilterChange={onCategoryFilterChange}
        />
        <PaymentTypeFilter
          selectedFilterId={filter.paymentType}
          paymentTypes={paymentTypes}
          onFilterChange={onPaymentTypeFilterChange}
        />
        <TaxesFilter
          withTaxes={filter.withTaxes}
          onFilterChange={onTaxesFilterChange}
        />

        {Object.keys(filter).length > 0 ? <FilterResetButton /> : ""}
      </>
    );
  };

  if (matchesPhone) {
    return (
      <FilterDialog
        open={mobileFilterOpened}
        onClose={() => setMobileFilterOpened(false)}
      >
        <FilterOpenedMobile>
          <FilterContent />
        </FilterOpenedMobile>
      </FilterDialog>
    );
  } else {
    return (
      <FilterOpened>
        <FilterContent />
      </FilterOpened>
    );
  }
};

interface OrderFilterProps {
  preLoadedFilter?: OrderFilterModel;

  onFilterChange: (filter: OrderFilterModel) => void;
  mobileFilterOpened: boolean;
  setMobileFilterOpened: React.Dispatch<React.SetStateAction<boolean>>;
}
